import { graphql } from "@opensea/graphql"
import { type BigNumber, bn } from "@opensea/utils"
import type { Client } from "urql"
import type { PublicClient } from "viem"
import {
  EVM_DEFAULT_FEE_ETH,
  EVM_DEFAULT_GAS_LIMIT,
  EVM_GAS_BUFFER_MULTIPLIER,
  GAS_BUFFER_CACHE_MINUTES,
  MINIMUM_GAS_PRICE_WEI,
  SOLANA_DEFAULT_FEE_SOL,
  SOLANA_FEE_BUFFER_MULTIPLIER,
  WEI_PER_ETH,
} from "../constants/gasBuffer"
import { getCacheEntry, setCacheEntry } from "./cache"

const fetchPromises: Record<string, Promise<BigNumber>> = {}

const solanaFeeQuery = graphql(`
  query EstimateSolanaFeeBuffer {
    estimateSolanaFeeBuffer
  }
`)

export async function getFeeBufferAmount(
  arch: "EVM" | "SVM" | null,
  chainIdentifier: string | null,
  balance: BigNumber,
  publicClient: PublicClient | null,
  gasLimit: number = EVM_DEFAULT_GAS_LIMIT,
  urqlClient?: Client | null,
): Promise<BigNumber> {
  if (!(arch && chainIdentifier)) {
    return bn(0)
  }

  const cacheKey = `${arch.toLowerCase()}FeeBuffer:${chainIdentifier}:${gasLimit}`

  const cached = getCacheEntry(cacheKey)
  if (cached) {
    return bn(cached)
  }

  if (cacheKey in fetchPromises) {
    return fetchPromises[cacheKey]
  }

  let promise: Promise<BigNumber>
  if (arch === "EVM") {
    if (!publicClient) {
      return bn(0)
    }
    promise = calculateEvmNativeGasBuffer(publicClient, balance, gasLimit)
  } else if (arch === "SVM") {
    if (!urqlClient) {
      return bn(0)
    }
    promise = calculateSvmNativeFeeBuffer(urqlClient)
  } else {
    return bn(0)
  }

  fetchPromises[cacheKey] = promise

  promise
    .then(buffer => {
      setCacheEntry(cacheKey, buffer.toString(), GAS_BUFFER_CACHE_MINUTES)
    })
    .catch(() => {
      // Cache miss on error - no action needed
    })
    .finally(() => {
      delete fetchPromises[cacheKey]
    })

  return promise
}

export async function calculateEvmNativeGasBuffer(
  publicClient: PublicClient,
  balance: BigNumber,
  gasLimit: number = EVM_DEFAULT_GAS_LIMIT,
): Promise<BigNumber> {
  if (!balance || balance.lte(0)) {
    return bn(0)
  }

  const defaultBuffer = bn(EVM_DEFAULT_FEE_ETH)
    .times(gasLimit)
    .times(EVM_GAS_BUFFER_MULTIPLIER)
    .div(EVM_DEFAULT_GAS_LIMIT)

  try {
    const feeData = await publicClient.estimateFeesPerGas()
    let gasPriceToUse = feeData.maxFeePerGas

    if (!gasPriceToUse || gasPriceToUse <= BigInt(0)) {
      gasPriceToUse = await publicClient.getGasPrice()
    }

    if (gasPriceToUse < BigInt(MINIMUM_GAS_PRICE_WEI)) {
      gasPriceToUse = BigInt(MINIMUM_GAS_PRICE_WEI)
    }

    const estimatedGasCostInWei = bn(gasPriceToUse.toString()).times(gasLimit)
    const bufferInWei = estimatedGasCostInWei.times(EVM_GAS_BUFFER_MULTIPLIER)
    const bufferInEth = bufferInWei.div(WEI_PER_ETH)

    return bufferInEth
  } catch (error) {
    console.error("Error calculating EVM native gas buffer:", error)
    return defaultBuffer
  }
}

export async function calculateSvmNativeFeeBuffer(
  urqlClient: Client,
): Promise<BigNumber> {
  const defaultBuffer = bn(SOLANA_DEFAULT_FEE_SOL).times(
    SOLANA_FEE_BUFFER_MULTIPLIER,
  )

  try {
    const { data, error } = await urqlClient.query(solanaFeeQuery, {})

    if (error || !data?.estimateSolanaFeeBuffer) {
      console.error("Error fetching Solana fee estimate:", error)
      return defaultBuffer
    }

    const feeEstimateInSol = bn(data.estimateSolanaFeeBuffer)
    const buffer = feeEstimateInSol.times(SOLANA_FEE_BUFFER_MULTIPLIER)

    return buffer
  } catch (error) {
    console.error("Error calculating SVM native fee buffer:", error)
    return defaultBuffer
  }
}
